"use client";

import React, { useState, useRef, useEffect } from "react";
import { SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Input } from "@/components/ui/input";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { EmptyResult } from "../components/EmptyResult";

interface FaqItem {
  question: string;
  answer: string;
  sectionTitle: string;
  sectionIndex: number;
  itemIndex: number;
  originalSectionIndex: number;
  originalItemIndex: number;
}

interface Faq1Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  canSearch?: boolean;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  faqSections?: {
    title?: string;
    faqItem?: {
      question?: string;
      answer?: string;
    }[];
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Faq1 = ({
  darkMode = false,
  variant = "A",
  spacing,
  canSearch = false,
  usedInPageNav = false,
  pageNavTitle = "Faq 1",
  themeChangeOnScroll = false,
  title,
  faqSections,
  attributes,
}: Faq1Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get all questions for autocomplete with section and item indices
  const allQuestions: FaqItem[] = faqSections?.flatMap((section, sectionIndex) => 
    section.faqItem?.map((faq, itemIndex) => ({
      question: faq.question || '',
      answer: faq.answer || '',
      sectionTitle: section.title || '',
      sectionIndex,
      itemIndex,
      originalSectionIndex: sectionIndex,
      originalItemIndex: itemIndex
    })) || []
  ) || [];

  // Filter questions for autocomplete
  const filteredQuestions = allQuestions.filter(question =>
    question.question.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter sections and questions based on search
  const filteredFaqSections = faqSections?.map(section => ({
    ...section,
    faqItem: section.faqItem?.filter(faq =>
      faq.question?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.faqItem && section.faqItem.length > 0);

  // Reset all search-related states
  const resetSearch = () => {
    // Reset all states in one batch
    setSearchTerm("");
    setIsOpen(false);
    setOpenAccordion([]);
    // Focus the input after reset
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Only open dropdown if there's content
    setIsOpen(value.length > 0);
  };

  const scrollToAccordion = (question: FaqItem) => {
    if (!filteredFaqSections) return;

    // Find the current position in the filtered list
    const currentSectionIndex = filteredFaqSections.findIndex(section => 
      section.faqItem?.some((faq: { question?: string }) => faq.question === question.question)
    );
    
    if (currentSectionIndex === -1) return;
    
    const currentSection = filteredFaqSections[currentSectionIndex];
    if (!currentSection?.faqItem) return;

    const currentItemIndex = currentSection.faqItem.findIndex(
      (faq: { question?: string }) => faq.question === question.question
    );
    
    if (currentItemIndex === -1) return;

    // Get the current question from filtered list to ensure we have the right reference
    const currentQuestion = currentSection.faqItem[currentItemIndex]?.question;
    if (!currentQuestion) return;

    // Find the accordion item
    const accordionElement = document.querySelector(`[data-question="${currentQuestion}"]`);
    
    if (accordionElement) {
      // Calculate position with offset
      const elementPosition = accordionElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 600;

      // Scroll to the position
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Open the accordion using the unique ID
      const uniqueId = `section-${currentSectionIndex}-item-${currentItemIndex}`;
      setOpenAccordion([uniqueId]);
    }
  };

  const handleSelect = (question: FaqItem) => {
    // Close dropdown first
    setIsOpen(false);
    
    // Update search term and focus
    setSearchTerm(question.question);
    inputRef.current?.focus();
    
    // Use the question object directly
    scrollToAccordion(question);
  };

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="faq1"
      attributes={attributes}
    >
      <div className="container">
        <div className="flex flex-col md:flex-row gap-6 mb-10 lg:mb-40 md:justify-between md:items-center" data-aos="fade-up">
          {title && (
            <h2 className="text-center sm:text-left">
              {parse(title)}
            </h2>
          )}
          {canSearch && (
            <div className="relative w-full md:w-80" ref={containerRef}>
              <div className="relative">
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchTerm}
                  onChange={handleInputChange}
                  onFocus={() => searchTerm.length > 0 && setIsOpen(true)}
                  onBlur={() => {
                    // Small delay to allow click events on suggestions to fire
                    setTimeout(() => setIsOpen(false), 200);
                  }}
                  className="w-full pl-9"
                />
                <span 
                  className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                  style={{ fontSize: '18px' }}
                >
                  search
                </span>
              </div>
              
              {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-popover rounded-md border shadow-md">
                  <Command className="rounded-lg border-none">
                    <CommandList>
                      {filteredQuestions.length > 0 ? (
                        <CommandGroup>
                          {filteredQuestions.map((item) => (
                            <CommandItem
                              key={`${item.originalSectionIndex}-${item.originalItemIndex}`}
                              onSelect={() => handleSelect(item)}
                              onMouseDown={(e) => {
                                // Prevent input blur when clicking suggestions
                                e.preventDefault();
                              }}
                              className="flex flex-col items-start gap-1 px-4 py-2 cursor-pointer"
                            >
                              <span className="font-medium">{item.question}</span>
                              <span className="text-muted-foreground paragraph-base">{item.sectionTitle}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ) : (
                        <CommandEmpty className="py-6 text-center text-muted-foreground">
                          No results found
                        </CommandEmpty>
                      )}
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
          )}
        </div>
        <div data-aos="fade-up" data-aos-delay="100">
          {filteredFaqSections && filteredFaqSections.length > 0 ? (
            filteredFaqSections.map((section, sectionIndex) => (
              <div className="grid gap-8 border-t py-8 md:grid-cols-2 md:gap-10" key={sectionIndex}>
                <h5>{section.title}</h5>
                <Accordion 
                  type="multiple" 
                  className="md:col-span-1"
                  value={openAccordion}
                  onValueChange={setOpenAccordion}
                >
                  {section.faqItem?.map((faq, itemIndex) => {
                    const uniqueId = `section-${sectionIndex}-item-${itemIndex}`;
                    return (
                    <AccordionItem 
                      key={itemIndex} 
                      value={uniqueId}
                      data-question={faq.question}
                    >
                      <AccordionTrigger 
                        className={cn(
                          "text-left py-6 !no-underline cursor-pointer text-wrap", 
                          itemIndex === 0 && '!pt-1'
                          )}
                      >
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="paragraph-base text-muted-foreground content-accordion">
                        {parse(faq.answer || '')}
                      </AccordionContent>
                    </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            ))
          ) : (
            <EmptyResult
              buttonOnClick={resetSearch}
            />
          )}
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Faq1 };